import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  DepartmentCreateInput,
  DepartmentDetails,
  DepartmentListFilters,
  DepartmentListItem,
  DepartmentSearchResult,
  DepartmentUpdateInput,
} from '../types/department';
import { CONFIGURATION_RECORD_INACTIVE } from '../constants/configuration';
import { nextDisplayOrder } from '../utils/displayOrder';
import { getMasterDataStatusLabel } from '../utils/masterDataStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

const DETAILS_PAGE_SIZE = 100;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) {
    throw Object.assign(new Error(`${label} is missing`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function requiredText(value: unknown, label: string): string {
  const text = textOrNull(value);
  if (!text) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return text;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeListItem(row: ObjectValue): DepartmentListItem | null {
  try {
    const isActive = boolOrNull(row.isActive);
    return {
      departmentId: requiredId(row.departmentId, 'departmentId'),
      departmentCode: textOrNull(row.departmentCode) ?? String(row.departmentId),
      departmentName: textOrNull(row.departmentName) ?? 'Department',
      description: textOrNull(row.description),
      locationId: numberOrNull(row.locationId),
      locationName: textOrNull(row.locationName),
      parentDepartmentId: numberOrNull(row.parentDepartmentId),
      parentDepartmentName: textOrNull(row.parentDepartmentName),
      displayOrder: numberOrNull(row.displayOrder),
      isActive,
      statusLabel: getMasterDataStatusLabel(isActive, textOrNull(row.statusCode)),
      rowVersion: numberOrNull(row.rowVersion),
    };
  } catch {
    return null;
  }
}

function normalizeDetails(row: ObjectValue): DepartmentDetails {
  const isActive = boolOrNull(row.isActive);
  return {
    departmentId: requiredId(row.departmentId, 'departmentId'),
    departmentCode: requiredText(row.departmentCode, 'departmentCode'),
    departmentName: requiredText(row.departmentName, 'departmentName'),
    description: textOrNull(row.description),
    locationId: numberOrNull(row.locationId),
    locationName: textOrNull(row.locationName),
    parentDepartmentId: numberOrNull(row.parentDepartmentId),
    parentDepartmentName: textOrNull(row.parentDepartmentName),
    displayOrder: numberOrNull(row.displayOrder),
    isActive,
    statusLabel: getMasterDataStatusLabel(isActive, textOrNull(row.statusCode)),
    rowVersion: requiredId(row.rowVersion, 'rowVersion'),
  };
}

function buildListParams(
  page: number,
  pageSize: number,
  filters: DepartmentListFilters = {},
) {
  // Organization department search rejects pageSize above 100.
  const safePageSize = Math.min(Math.max(pageSize, 1), 100);
  const params: Record<string, string | number> = {
    page,
    pageSize: safePageSize,
  };
  const search = filters.search?.trim();
  if (search) params.search = search;
  if (filters.locationId && filters.locationId > 0) {
    params.locationId = filters.locationId;
  }
  if (filters.isActive === true) params.isActive = 1;
  if (filters.isActive === false) params.isActive = 0;
  return params;
}

export async function getDepartments(options: {
  page?: number;
  pageSize?: number;
  filters?: DepartmentListFilters;
} = {}): Promise<DepartmentSearchResult> {
  const page = options.page && options.page > 0 ? options.page : 1;
  const pageSize =
    options.pageSize && options.pageSize > 0 ? options.pageSize : 20;

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: number;
        pageSize?: number;
        totalRecords?: number;
        totalPages?: number;
      };
    }>
  >(API_ENDPOINTS.DEPARTMENTS.LIST, {
    params: buildListParams(page, pageSize, options.filters),
  });

  const data = response.data.data ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeListItem)
        .filter((item): item is DepartmentListItem => item != null)
    : [];
  const pagination = data.pagination ?? {};
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const resolvedPageSize = numberOrNull(pagination.pageSize) ?? pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / resolvedPageSize));

  return {
    items,
    page: numberOrNull(pagination.page) ?? page,
    pageSize: resolvedPageSize,
    totalItems,
    totalPages,
  };
}

/**
 * Loads every active department for the signed-in organization by paging the
 * real departments API (max 100 per request).
 */
export async function getAllActiveDepartments(): Promise<DepartmentListItem[]> {
  const pageSize = 100;
  const first = await getDepartments({
    page: 1,
    pageSize,
    filters: { isActive: true },
  });
  const items = [...first.items];
  const totalPages = Math.max(1, first.totalPages || 1);
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await getDepartments({
      page,
      pageSize,
      filters: { isActive: true },
    });
    items.push(...next.items);
  }
  return items;
}

/** No dedicated GET-by-id. Search pages until the department is found. */
export async function getDepartmentDetails(
  departmentId: number,
): Promise<DepartmentDetails> {
  if (!Number.isInteger(departmentId) || departmentId <= 0) {
    throw Object.assign(new Error('departmentId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const response = await apiClient.get<
      Envelope<{
        items?: ObjectValue[];
        pagination?: { totalPages?: number; totalRecords?: number };
      }>
    >(API_ENDPOINTS.DEPARTMENTS.LIST, {
      params: { page, pageSize: DETAILS_PAGE_SIZE },
    });
    const data = response.data.data ?? {};
    const items = Array.isArray(data.items) ? data.items : [];
    const match = items.find(
      row => numberOrNull(row.departmentId) === departmentId,
    );
    if (match) return normalizeDetails(match);

    const totalRecords = numberOrNull(data.pagination?.totalRecords) ?? 0;
    totalPages =
      numberOrNull(data.pagination?.totalPages) ??
      Math.max(1, Math.ceil(totalRecords / DETAILS_PAGE_SIZE));
    page += 1;
  }

  throw Object.assign(new Error('Department was not found.'), {
    code: 'DEPARTMENT_NOT_FOUND',
  });
}

export async function createDepartment(
  input: DepartmentCreateInput,
): Promise<DepartmentDetails> {
  const existing = await getDepartments({ page: 1, pageSize: DETAILS_PAGE_SIZE });
  const body: Record<string, unknown> = {
    departmentCode: input.departmentCode.trim(),
    departmentName: input.departmentName.trim(),
    description: emptyToNull(input.description),
    displayOrder: nextDisplayOrder(existing.items, existing.totalItems),
  };
  if (input.locationId && input.locationId > 0) {
    body.locationId = input.locationId;
  }
  if (input.parentDepartmentId && input.parentDepartmentId > 0) {
    body.parentDepartmentId = input.parentDepartmentId;
  }

  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.DEPARTMENTS.CREATE,
    body,
  );
  return normalizeDetails({
    ...body,
    isActive: true,
    rowVersion: 1,
    ...(response.data.data ?? {}),
  });
}

export async function updateDepartment(
  departmentId: number,
  input: DepartmentUpdateInput,
): Promise<DepartmentDetails> {
  if (!Number.isInteger(departmentId) || departmentId <= 0) {
    throw Object.assign(new Error('departmentId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const body: Record<string, unknown> = {
    departmentName: input.departmentName.trim(),
    description: emptyToNull(input.description),
    displayOrder: input.displayOrder ?? 0,
    isActive: input.isActive,
    rowVersion: input.rowVersion,
  };
  if (input.locationId && input.locationId > 0) {
    body.locationId = input.locationId;
  }
  if (input.parentDepartmentId && input.parentDepartmentId > 0) {
    if (input.parentDepartmentId === departmentId) {
      throw Object.assign(
        new Error('A department cannot be its own parent.'),
        { code: 'VALIDATION_ERROR' },
      );
    }
    body.parentDepartmentId = input.parentDepartmentId;
  }

  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.DEPARTMENTS.UPDATE(departmentId),
    body,
  );
  return normalizeDetails({
    departmentId,
    departmentCode: '—',
    ...body,
    ...(response.data.data ?? {}),
  });
}

export async function deactivateDepartment(
  item: DepartmentDetails,
): Promise<DepartmentDetails> {
  return updateDepartment(item.departmentId, {
    locationId: item.locationId,
    parentDepartmentId: item.parentDepartmentId,
    departmentName: item.departmentName,
    description: item.description,
    displayOrder: item.displayOrder ?? 0,
    isActive: CONFIGURATION_RECORD_INACTIVE,
    rowVersion: item.rowVersion,
  });
}
